/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2022 (original work) Open Assessment Technologies SA
 */

import { writable, get } from 'svelte/store';
import { recoverableErrors as errorKeys } from '@/core/utils/consts';
import { activeRibbon } from '@/store/ribbon';
import { showNotification } from '@oat-sa-private/ui-components';
import dynamicKeys from '@/locale/dynamicKeys';

/**
 * List of handled recoverable errors
 * recoverableError.status - If error is active or not
 * recoverableError.retrying - If error is currently trying to be resolved
 */
export const recoverableErrors = writable({
    [errorKeys.internetConnection]: {
        status: false,
        retrying: false
    },
    [errorKeys.websocket]: {
        status: false,
        retrying: false
    },
    [errorKeys.nrps]: {
        status: false,
        retrying: false
    },
    [errorKeys.interactions]: {
        status: false,
        retrying: false
    }
});

/**
 * Updates given errors with the given status. If status is false (so error successfully recovered)
 * ribbon is disabled and a notification is sent. Notification key is taken from @dynamicKeys.
 * If Status is true, correct ribbon preset is selected. Ribbon presets must
 *  always be named as "[errorKey].detected" and "[errorKey].failure"
 * @param {string | Array} error - error to update, must be a valid @errorKeys value or an array of @errorKeys
 * @param {Object} value - state to set on error
 * @param {Boolean} value.status - If error is active or not
 * @param {Boolean} value.retrying - If error is currently trying to be resolved
 */
export function update(error, value) {
    recoverableErrors.update(n => {
        if (Array.isArray(error)) {
            for (const element of error) n[element] = value;
            error = errorKeys.multiCause;
        } else {
            n[error] = value;
        }

        if (value.status === false) {
            activeRibbon.set('');
            showNotification({
                message: dynamicKeys()[`${error}-error-solved`]
            });
        } else {
            value.retrying ? activeRibbon.set(`${error}.detected`) : activeRibbon.set(`${error}.failure`);
        }
        return n;
    });
}
/**
 * Resets recoverableErrors store value. If no @errors provided, every error is set to .status false
 * and .retrying false. If @errors is given, only are reset received keys. If active ribbon preset
 * belongs to a reseted error, it is removed.
 * @param {Array} errors - Array of strings. Strings must be a valid @errorKeys value
 */
export function reset(errors) {
    recoverableErrors.update(n => {
        Object.keys(n).forEach(key => {
            if (!errors || errors.find(err => err === key)) {
                n[key].status = false;
                n[key].retrying = false;
                if (!errors || get(activeRibbon).split('.')[0] === key) {
                    activeRibbon.set('');
                }
            }
        });

        return n;
    });
}
